import { Checkbox, Intent } from "@blueprintjs/core";
import { ButtonContainer, IDataTableColumn, showError, showSuccess } from "nsitools-react";
import * as React from "react";
import { useHistory } from "react-router";
import { Link } from "react-router-dom";

import {
  ERequestState,
  ERequestType,
  HealthTestRequestApi,
  HealthTestRequestGridDto,
  HealthTestRequestSearch,
  RequestApi,
} from "../../../../api";
import { AddButton, CloseButton, EditButton, SearchTablePage, ViewButton } from "../../../../components";
import { useDialog, useEventsContext, useGlobalData } from "../../../../contexts";
import { ERoutes } from "../../../../ERoutes";
import { useApiService, useTl } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";
import { getRouteStr } from "../../../../utils/RequestRouteUtil";

export interface IHealthTestRequestListProps {}

export const HealthTestRequestList: React.FunctionComponent<IHealthTestRequestListProps> = (props) => {
  const api = useApiService(HealthTestRequestApi);
  const requestApi = useApiService(RequestApi);
  const { dispatchEvent } = useEventsContext();
  const [selectAll, setselectAll] = React.useState(false);
  const { t } = useTl();
  const history = useHistory();
  const { currentLanguage } = useGlobalData();

  const selectionList = React.useMemo(() => [], []);
  const closableIdsList = React.useMemo(() => [], []);
  const { showDialog } = useDialog();

  const toggleSelectAll = React.useCallback(() => {
    const value = !selectAll;
    if (value) {
      selectionList.splice(0, selectionList.length, ...closableIdsList);
    } else {
      selectionList.length = 0;
    }
    setselectAll(value);
  }, [closableIdsList, selectAll, selectionList]);

  const columnCheckBox = React.useCallback(
    (row: HealthTestRequestGridDto) => {
      const disabled = row.requestStateCode !== ERequestState.EVALUATED;
      if (!disabled) {
        closableIdsList.push(row.requestId);
      }
      return (
        <Checkbox
          checked={selectionList.find((c) => c === row.requestId)}
          onChange={() => {
            if (selectionList.includes(row.requestId)) {
              const index = selectionList.findIndex((e) => e === row.requestId);
              selectionList.splice(index, 1);
            } else {
              selectionList.push(row.requestId);
            }
          }}
          disabled={disabled}
        />
      );
    },
    [closableIdsList, selectionList]
  );

  const redirectToRequest = React.useCallback(
    async (requestId: number, requestType: ERequestType) => {
      const subRequestId = await requestApi.getSubRequestTypeId(requestId, requestType);
      const link = "#" + (await getRouteStr(requestType, subRequestId, "view"));
      console.log("link ", link);
      const anchor: HTMLAnchorElement = document.createElement("a");
      anchor.href = link;
      anchor.target = "_blank";
      anchor.click();
      setTimeout(() => URL.revokeObjectURL(link));
    },
    [requestApi]
  );

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        header: () => <Checkbox onChange={toggleSelectAll} />,
        computed: true,
        disableSorting: true,
        fieldName: "selector",
        autoFitContent: true,
        render: columnCheckBox,
      },
      {
        computed: true,
        disableSorting: true,
        fieldName: "actions",
        autoFitContent: true,
        render: (row: HealthTestRequestGridDto) => (
          <ButtonContainer>
            <ViewButton
              minimal={true}
              onClick={() => history.push(`${ERoutes.healthTestRequest}/${row.healthTestRequestId}/detail/view`)}
            />
            <EditButton
              minimal={true}
              disabled={row.requestStateCode === ERequestState.EXECUTED}
              onClick={() => history.push(`${ERoutes.healthTestRequest}/${row.healthTestRequestId}/detail/edit`)}
            />
          </ButtonContainer>
        ),
      },
      {
        header: () => t(ETLCodes.RequestBlocked),
        fieldName: "isBlocked",
        alignment: "center",
        render: (row: HealthTestRequestGridDto) => <Checkbox checked={row.isBlocked} disabled />,
      },
      {
        header: () => t(ETLCodes.RequestNumber),
        fieldName: "requestNumber",
      },
      {
        header: () => t(ETLCodes.ParentRequest),
        fieldName: "parentRequestNumber",
        render: (row: HealthTestRequestGridDto) => {
          if (row.parentRequestId) {
            return (
              // eslint-disable-next-line jsx-a11y/anchor-is-valid
              <a href="#" onClick={() => redirectToRequest(row.parentRequestId, row.parentRequestType)}>
                {row.parentRequestNumber}
              </a>
            );
          } else {
            return "/";
          }
        },
      },
      {
        header: () => t(ETLCodes.RequestDate),
        fieldName: "requestDate",
      },
      {
        header: () => t(ETLCodes.RequestState),
        fieldName: "requestState",
      },
      {
        header: () => t(ETLCodes.Dog),
        fieldName: "dog",
        render: (row: HealthTestRequestGridDto) => (
          <Link target="_blank" to={`${ERoutes.dog}/${row.dogId}/detail/view`}>
            {row.dogName}
          </Link>
        ),
      },
      {
        header: () => t(ETLCodes.Contact),
        fieldName: "contact",
        render: (row: HealthTestRequestGridDto) => (
          <Link target="_blank" to={`${ERoutes.people}/${row.contactId}/detail/view`}>
            {row.contact}
          </Link>
        ),
      },
      {
        header: () => t(ETLCodes.Lab),
        fieldName: "lab",
      },
      {
        header: () => t(ETLCodes.Invoice),
        fieldName: "invoiceNumber",
      },
    ],
    [columnCheckBox, history, redirectToRequest, t, toggleSelectAll]
  );

  const closeRequests = React.useCallback(
    (requestList) => {
      if (selectionList.length === 0) {
        showDialog({
          message: t(ETLCodes.CloseRequestSelectionEmpty),
          title: t(ETLCodes.CloseRequests),
          buttons: [
            {
              resultType: "yes",
              text: t(ETLCodes.General_Ok),
              intent: Intent.PRIMARY,
            },
          ],
          onConfirmed: async () => {},
        });
      } else {
        showDialog({
          message: t(ETLCodes.CloseRequestConfirmationMessage),
          title: t(ETLCodes.CloseRequests),
          onConfirmed: async () => {
            try {
              await requestList.forEach((element) => {
                api.close(element);
              });
              selectionList.splice(0, selectionList.length);
              dispatchEvent("RefreshSearchTables", 0);
              showSuccess(t(ETLCodes.RequestsClosedSuccess));
            } catch (error) {
              showError(t(ETLCodes.Error));
            }
          },
        });
      }
    },
    [api, dispatchEvent, selectionList, showDialog, t]
  );

  const addItemFunction = React.useCallback(() => {
    history.push(`${ERoutes.healthTestRequest}/0/detail/edit`);
  }, [history]);

  const search = React.useCallback(
    (sObj) =>
      api.baseSearch(
        HealthTestRequestSearch.fromJS({
          ...sObj,
          languageId: currentLanguage.languageId,
        })
      ),
    [api, currentLanguage.languageId]
  );

  return (
    <SearchTablePage
      columns={columns}
      getCriteriasFunction={() => api.getSearchCriterias(true)}
      searchFunction={search}
      exportFunction={(sObj) =>
        api.exportXlsFile(
          HealthTestRequestSearch.fromJS({
            ...sObj,
            languageId: currentLanguage.languageId,
          })
        )
      }
      sortKeys={{ requestDate: "DESC", requestNumber: "DESC" }}
      breadCrumbs={[{ text: t(ETLCodes.HealthTestRequests) }]}
      rightElement={
        <div>
          <AddButton text={t(ETLCodes.General_Add)} onClick={() => addItemFunction()} />
          <CloseButton text={t(ETLCodes.Close)} onClick={() => closeRequests(selectionList)} />
        </div>
      }
    />
  );
};
